import { resolveURL, withoutLeadingSlash } from 'ufo'
import { useRequestURL, useError } from '#imports'

/**
 * Middleware to handle redirection of short Product Detail Page (PDP) URLs.
 *
 * This middleware resolves and redirects short product URLs with 'pcode' parameter
 * to their full, localized product URLs. It only works on server-side and index routes.
 *
 * @returns {void|NavigationFailure} Redirects to full product URL if short URL is detected
 * @throws {Error} If there are issues fetching the product URL
 *
 * @example
 * // Automatically resolves and redirects short product URLs
 * export default defineNuxtRouteMiddleware(async (to) => { ... })
 *
 * @note
 * - Only works on server-side and index routes
 * - Uses Algolia index and current language for URL resolution
 * - Performs a full page reload to prevent infinite redirects
 */
export default defineNuxtRouteMiddleware(async to => {
  if (import.meta.prerender || import.meta.client) return

  const isIndex = to.name?.toString() === 'localized-index'
  const error = useError()
  const url = useRequestURL()

  if (!url.searchParams.has('pcode') || !isIndex || error.value) return

  const appConfig = useAppConfig()
  const language = appConfig.currentLanguage
  const { currentAlgoliaIndex } = appConfig

  const pcode = url.searchParams.get('pcode')?.replaceAll('-', '_')
  if (!pcode) return

  const productUrl = await $fetch(
    `/api/getProductUrl/${currentAlgoliaIndex}/${pcode}?lang=${language}`
  )
  const localizedProductUrl = resolveURL(
    appConfig.currentLanguageCountry,
    productUrl
  )

  const originalUrl = withoutLeadingSlash(url.pathname)
  if (localizedProductUrl === originalUrl) return
  // external: true is needed for a full page reload to avoid infinite redirects
  return navigateTo(localizedProductUrl, { external: true })
})
